import React, { useState } from "react";
import { User, Zap, AlertCircle } from "lucide-react";
import "../styles/Quote.css";
import Swal from "sweetalert2";
import axios from "axios";

export default function QuoteStepper({ isOpen, onClose, selectedProduct, selectedEquipment }) {
  const steps = ["Informations personnelles", "Kit & Emplacement"];
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    message: "",
    customertype: "",
    quoteType: "",
    places: "Antananarivo",
    provinceName: "",
    solarKitType: "",
    budget: "",
    equipments: selectedEquipment || [],
    watt: selectedProduct?.puissance || "",
    product: selectedProduct?.name || ""
  });

  const [errors, setErrors] = useState({});

  const clearFormData = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      address: "",
      message: "",
      customertype: "",
      quoteType: "",
      places: "Antananarivo",
      provinceName: "",
      solarKitType: "",
      budget: "",
      equipments: selectedEquipment || [],
      watt: selectedProduct?.puissance || "",
      product: selectedProduct?.name || ""
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateStep = () => {
    let newErrors = {};
    if (currentStep === 0) {
      if (!form.name.trim()) newErrors.name = "Le nom est requis.";
      if (!form.email.trim()) {
        newErrors.email = "L'email est requis.";
      } else if (!/\S+@\S+\.\S+/.test(form.email)) {
        newErrors.email = "Format email invalide.";
      }
      if (!form.phone.trim()) {
        newErrors.phone = "Le téléphone est requis.";
      } else if (!/^[0-9]{9,}$/.test(form.phone)) {
        newErrors.phone = "Numéro invalide (au moins 9 chiffres).";
      }
      if (!form.address.trim()) newErrors.address = "L'adresse est requise.";
    }
    if (currentStep === 1) {
      if (!form.customertype) newErrors.customertype = "Veuillez sélectionner Particulier ou Société.";
      if (!form.quoteType) newErrors.quoteType = "Veuillez sélectionner Avec ou Sans installation.";
      if (form.places === "province" && !form.provinceName.trim()) {
        newErrors.provinceName = "Précisez la province.";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) {
      if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    if (currentStep!=1) return;
    setIsLoading(true);
    let finalData = {
      ...form,
      places: form.places === "province" ? `Province ${form.provinceName}` : form.places,
      equipments: selectedEquipment,
      watt: selectedProduct?.puissance+'W'
    };
    const URL = 'https://devis.greenline-madagascar.com/api/'
    axios.post(URL+'qsubmitSimulatorContactDetails', finalData)
      .then((res) => {
        if (res?.data) {
          Swal.fire({
            title: "Demande envoyée avec succès",
            text: "Notre équipe vous contactera dans les plus brefs délais.",
            icon: "success",
            timer: 3000,
            showConfirmButton: true,
            confirmButtonText: "Fermer",
            timerProgressBar: true,
            didOpen: (popup) => {
              popup.addEventListener("mouseenter", Swal.stopTimer);
              popup.addEventListener("mouseleave", Swal.resumeTimer);
            }
          });
          onClose();
          clearFormData();
        }
      }).catch((error) => {
        onClose();
        Swal.fire({
          title: "Une erreur est survenue",
          text: "Veuillez réessayer. Si le problème persiste, contactez notre service client.",
          icon: "error",
          timer: 3000,
          showConfirmButton: true,
          confirmButtonText: "Fermer",
          timerProgressBar: true,
          didOpen: (popup) => {
            popup.addEventListener("mouseenter", Swal.stopTimer);
            popup.addEventListener("mouseleave", Swal.resumeTimer);
          }
        });
      })
      .finally(() => setIsLoading(false));
  };

  if (!isOpen) return null;

  return (
    <div className="quote-stepper-overlay" onClick={onClose}>
      <div className="quote-stepper" onClick={(e) => e.stopPropagation()}>
        <div className="quote-stepper-header">
          <h2>Demande de devis</h2>
          {selectedProduct && (
            <input 
              type="text" 
              value={selectedProduct.name} 
              disabled 
              className="selected-product-input"
            />
          )}
          <button className="quote-stepper-close" onClick={onClose}>✕</button>
        </div>

        {/* Stepper indicator */}
        <div className="quote-stepper-indicator">
          <div className={`step ${currentStep > 0 ? "completed" : ""} ${currentStep === 0 ? "active" : ""}`}>
            <User size={36} />
            <span>Informations personnelles</span>
          </div>
          <div className="step-connector"></div>
          <div className={`step ${currentStep > 1 ? "completed" : ""} ${currentStep === 1 ? "active" : ""}`}>
            <Zap size={36} />
            <span>Kit & Emplacement</span>
          </div>
        </div>
        <div className="quote-stepper-content">
            {/* Form */}
            <form className="quote-stepper-form">
              {currentStep === 0 && (
                <>
                  <div className={`quote-field ${errors.name ? "has-error" : ""}`}>
                    <label>Nom*</label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} />
                    {errors.name && <span className="error-msg"><AlertCircle size={14}/> {errors.name}</span>}
                  </div>
                  <div className={`quote-field ${errors.email ? "has-error" : ""}`}>
                    <label>Email*</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} />
                    {errors.email && <span className="error-msg"><AlertCircle size={14}/> {errors.email}</span>}
                  </div>
                  <div className={`quote-field ${errors.phone ? "has-error" : ""}`}>
                    <label>Téléphone*</label>
                    <input type="tel" name="phone" value={form.phone} onChange={handleChange} />
                    {errors.phone && <span className="error-msg"><AlertCircle size={14}/> {errors.phone}</span>}
                  </div>
                  <div className={`quote-field ${errors.address ? "has-error" : ""}`}>
                    <label>Adresse*</label>
                    <input type="text" name="address" value={form.address} onChange={handleChange} />
                    {errors.address && <span className="error-msg"><AlertCircle size={14}/> {errors.address}</span>}
                  </div>
                  <div className="quote-field">
                    <label>Message</label>
                    <textarea name="message" value={form.message} onChange={handleChange} rows="3" />
                  </div>
                </>
              )}

              {currentStep === 1 && (
                <>
                  <div className={`quote-field ${errors.customertype ? "has-error" : ""}`}>
                    <label>Je suis un* :</label>
                    <div className="radio-group">
                      <label>
                        <input
                          type="radio"
                          name="customertype"
                          value="Particulier"
                          checked={form.customertype === "Particulier"}
                          onChange={handleChange}
                        />
                        Particulier
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="customertype"
                          value="Societe"
                          checked={form.customertype === "Societe"}
                          onChange={handleChange}
                        />
                        Société
                      </label>
                    </div>
                    {errors.customertype && <span className="error-msg"><AlertCircle size={14}/> {errors.customertype}</span>}
                  </div>

                  <div className={`quote-field ${errors.quoteType ? "has-error" : ""}`}>
                    <label>Je veux un devis* :</label>
                    <div className="radio-group">
                      <label>
                        <input
                          type="radio"
                          name="quoteType"
                          value="Avec installation"
                          checked={form.quoteType === "Avec installation"}
                          onChange={handleChange}
                        />
                        Avec installation
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="quoteType"
                          value="Sans installation"
                          checked={form.quoteType === "Sans installation"}
                          onChange={handleChange}
                        />
                        Sans installation
                      </label>
                    </div>
                    {errors.quoteType && <span className="error-msg"><AlertCircle size={14}/> {errors.quoteType}</span>}
                  </div>

                  <div className="quote-field">
                    <label>Lieux* :</label>
                    <select
                      name="places"
                      value={form.places}
                      onChange={handleChange}
                    >
                      <option value="Antananarivo">Antananarivo</option>
                      <option value="province">Province</option>
                    </select>
                  </div>

                  {form.places === "province" && (
                    <div className={`quote-field ${errors.provinceName ? "has-error" : ""}`}>
                      <label>Précisez la province*</label>
                      <input
                        type="text"
                        name="provinceName"
                        value={form.provinceName}
                        onChange={handleChange}
                      />
                      {errors.provinceName && <span className="error-msg"><AlertCircle size={14}/> {errors.provinceName}</span>}
                    </div>
                  )}

                  <div className="quote-field">
                    <label>Je veux un kit solaire :</label>
                    <div className="radio-group">
                      <label>
                        <input
                          type="radio"
                          name="solarKitType"
                          value="Hybride avec la jirama"
                          checked={form.solarKitType === "Hybride avec la jirama"}
                          onChange={handleChange}
                        />
                        Hybride avec la jirama
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="solarKitType"
                          value="100% Autonome"
                          checked={form.solarKitType === "100% Autonome"}
                          onChange={handleChange}
                        />
                        100% Autonome
                      </label>
                    </div>
                  </div>

                  <div className="quote-field">
                    <label>Mon Budget est de ( Ariary ) :</label>
                    <select name="budget" value={form.budget} onChange={handleChange}>
                      <option value="">Sélectionnez</option>
                      <option value="- de 5 millions">- de 5 millions</option>
                      <option value="5 à 10 millions">5 à 10 millions</option>
                      <option value="10 à 15 millions">10 à 15 millions</option>
                      <option value="15 à 20 millions">15 à 20 millions</option>
                      <option value="20 à 30 millions">20 à 30 millions</option>
                      <option value="+ de 30 millions">+ de 30 millions</option>
                    </select>
                  </div>
                </>
              )}

              {/* Navigation */}
              <div className="quote-stepper-buttons">
                {currentStep > 0 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="btn-prev"
                    disabled={isLoading}
                  >
                    ← Précédent
                  </button>
                )}
                {currentStep < steps.length - 1 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn-next"
                    disabled={isLoading}
                  >
                    Suivant →
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="btn-submit"
                    disabled={isLoading}
                  >
                    {isLoading ? "Envoi en cours..." : "Envoyer"}
                  </button>
                )}
              </div>
            </form>
        </div>
      </div>
    </div>
  );
}
